// src/rms/agreements/library/common.js
// ============================================================================
// The clause spine every trade shares
// ============================================================================
// NOT LEGAL ADVICE. These are drafting aids assembled from publicly published
// commercial templates and from what the ESIGN Act and UETA actually require of
// an electronic signature. Nobody's counsel has reviewed them, in any
// jurisdiction, for any trade. An operator is expected to read every word and
// change what does not fit their business and their local law -- which is the
// entire reason the editor exists.
//
// ----------------------------------------------------------------------------
// Why a spine and deltas rather than twelve whole documents
// ----------------------------------------------------------------------------
// Sixteen of the seventeen clauses below are the same argument in every trade:
// who the parties are, what is being let, when it comes back, who pays for it
// when it does not. Writing them twelve times would guarantee that a fix to the
// indemnity clause reached eleven of the twelve.
//
// What genuinely differs per trade is a handful of clauses -- fuel and mileage
// for a car, passenger capacity for a boat, a lien for a storage unit -- and
// those live in the vertical's own file, spliced in at VERTICAL_SLOT.
//
// ----------------------------------------------------------------------------
// Numbering
// ----------------------------------------------------------------------------
// Headings carry no numbers here. index.js numbers them at assembly, so
// inserting a trade clause in the middle does not leave the owner with two
// clause 7s. An owner who edits the body afterwards owns the numbering from
// then on -- the stored body is plain text, exactly as it will be signed.
// ============================================================================

/** A string in the two languages the starter library ships in. */
export const t2 = (en, fr) => ({ en, fr });

/** Where a vertical's own clauses are spliced into the spine. */
export const VERTICAL_SLOT = "@vertical";

export const SPINE = [
  "parties",
  "theItem",
  "term",
  "charges",
  "deposit",
  "permittedUse",
  VERTICAL_SLOT,
  "care",
  "lossDamage",
  "insurance",
  "indemnity",
  "lateReturn",
  "cancellation",
  "default",
  "privacy",
  "governingLaw",
  "entire",
  "esign",
];

export const SHARED = {
  parties: {
    heading: t2("Parties", "Parties"),
    body: t2(
      `This agreement is made on {{today}} between {{business_name}} of {{business_address}} (the "Owner") and {{renter_name}} of {{renter_address}} (the "Renter").

The Owner can be reached on {{business_phone}} or at {{business_email}}. Any notice under this agreement may be given to the Owner at those details, and to the Renter at the details below.

The Renter's contact details on file are {{renter_email}} and {{renter_phone}}. The identification recorded for this rental is {{licence_number}}. The Renter confirms these details are accurate and undertakes to tell the Owner promptly if any of them change during the rental.`,
      `Le présent contrat est conclu le {{today}} entre {{business_name}}, dont l'adresse est {{business_address}} (le « Loueur »), et {{renter_name}}, dont l'adresse est {{renter_address}} (le « Locataire »).

Le Loueur peut être joint au {{business_phone}} ou à l'adresse {{business_email}}. Tout avis donné au titre du présent contrat peut être adressé au Loueur à ces coordonnées et au Locataire à celles indiquées ci-dessous.

Les coordonnées du Locataire au dossier sont {{renter_email}} et {{renter_phone}}. La pièce d'identité enregistrée pour cette location porte le numéro {{licence_number}}. Le Locataire confirme l'exactitude de ces renseignements et s'engage à informer le Loueur sans délai de tout changement pendant la durée de la location.`
    ),
  },

  theItem: {
    heading: t2("What is being rented", "Objet de la location"),
    body: t2(
      `The Owner rents to the Renter the following item, and no other: {{asset}} (unit {{asset_code}}), collected from {{pickup_location}}. This rental is recorded under reference {{reservation_number}}.

The Renter has had the opportunity to inspect the item before taking it and accepts it in the condition recorded at handover. Any condition report, photographs or readings taken at handover form part of this agreement.`,
      `Le Loueur loue au Locataire le bien suivant, à l'exclusion de tout autre : {{asset}} (unité {{asset_code}}), à récupérer à {{pickup_location}}. La présente location est enregistrée sous la référence {{reservation_number}}.

Le Locataire a eu la possibilité d'inspecter le bien avant sa prise en charge et l'accepte dans l'état constaté lors de la remise. Tout état des lieux, photographie ou relevé effectué à la remise fait partie intégrante du présent contrat.`
    ),
  },

  term: {
    heading: t2("Rental period", "Durée de la location"),
    body: t2(
      `The rental begins at {{starts_at}} and ends at {{ends_at}}. The item must be returned to {{pickup_location}} by the end of that period unless the Owner has agreed in writing to an extension.

An extension must be requested before the rental period ends. The Owner is not obliged to grant one, and may refuse if the item is already promised to another customer.`,
      `La location commence le {{starts_at}} et se termine le {{ends_at}}. Le bien doit être restitué à {{pickup_location}} au plus tard à l'échéance, sauf prolongation acceptée par écrit par le Loueur.

Toute prolongation doit être demandée avant la fin de la période de location. Le Loueur n'est pas tenu de l'accorder et peut la refuser si le bien est déjà réservé par un autre client.`
    ),
  },

  charges: {
    heading: t2("Charges and payment", "Prix et paiement"),
    body: t2(
      `The agreed charge for this rental is {{total}}, based on a rate of {{daily_rate}} plus any add-ons, taxes and fees itemised on the booking.

Charges that cannot be known until the item comes back -- additional days, fuel, cleaning, distance or usage over the included allowance, damage, and any fines or charges incurred during the rental -- are billed on return. The Renter authorises the Owner to charge these to the payment method held on file.

Amounts are payable in the currency shown on the booking. An invoice will be issued for every amount charged.`,
      `Le prix convenu pour la présente location est de {{total}}, sur la base d'un tarif de {{daily_rate}}, majoré des options, taxes et frais détaillés sur la réservation.

Les sommes qui ne peuvent être établies qu'au retour du bien — journées supplémentaires, carburant, nettoyage, distance ou usage au-delà du forfait inclus, dommages, ainsi que toute amende ou tout frais encouru pendant la location — sont facturées à la restitution. Le Locataire autorise le Loueur à les prélever sur le moyen de paiement enregistré.

Les sommes sont payables dans la devise indiquée sur la réservation. Une facture est émise pour chaque montant prélevé.`
    ),
  },

  deposit: {
    heading: t2("Security deposit", "Dépôt de garantie"),
    body: t2(
      `A security deposit of {{deposit}} is taken before the item is handed over.

The Owner may apply the deposit against any amount the Renter owes under this agreement, including unpaid charges, damage, cleaning, missing items, fuel, and fines. Anything not applied is returned after the item has been checked in and inspected.

The deposit is not a limit on what the Renter owes. Where the amount due exceeds the deposit, the Renter remains liable for the balance.`,
      `Un dépôt de garantie de {{deposit}} est exigé avant la remise du bien.

Le Loueur peut imputer sur ce dépôt toute somme due par le Locataire au titre du présent contrat, notamment les sommes impayées, les dommages, le nettoyage, les éléments manquants, le carburant et les amendes. Le solde non imputé est restitué après retour et inspection du bien.

Le dépôt ne constitue pas un plafond de responsabilité. Si les sommes dues excèdent le dépôt, le Locataire demeure redevable de la différence.`
    ),
  },

  permittedUse: {
    heading: t2("Permitted use", "Usage autorisé"),
    body: t2(
      `The item may be used only for its ordinary purpose, only by the Renter and anyone the Owner has named in writing on this agreement, and only in accordance with the manufacturer's instructions and all applicable law.

The Renter must not sub-let, lend, pledge, sell or part with possession of the item, and must not use it to carry passengers or goods for hire unless this agreement says otherwise in writing.

The Renter must not modify, repair or alter the item, remove any identifying mark, or tamper with any meter, lock, tracker or safety device fitted to it.`,
      `Le bien ne peut être utilisé que conformément à sa destination normale, par le seul Locataire et par les personnes que le Loueur a désignées par écrit au présent contrat, et dans le respect des instructions du fabricant et de la réglementation applicable.

Le Locataire ne peut ni sous-louer, ni prêter, ni donner en gage, ni vendre le bien, ni s'en dessaisir de quelque manière que ce soit, ni l'utiliser pour le transport rémunéré de personnes ou de marchandises, sauf stipulation écrite contraire.

Le Locataire ne peut ni modifier, ni réparer, ni transformer le bien, ni supprimer une marque d'identification, ni altérer un compteur, une serrure, un dispositif de repérage ou un organe de sécurité qui y serait installé.`
    ),
  },

  care: {
    heading: t2("Care of the item", "Entretien du bien"),
    body: t2(
      `The Renter must keep the item secure and in good order for the whole of the rental period, protect it from weather and theft, and return it in the condition in which it was received, allowing for fair wear and tear.

Fair wear and tear means the deterioration that comes from normal, careful use. It does not cover damage from misuse, neglect, overloading, use in conditions the item is not built for, or failure to follow the manufacturer's instructions.

The Renter must tell the Owner as soon as anything goes wrong with the item, and must not continue using it once a fault appears that makes it unsafe.`,
      `Le Locataire doit conserver le bien en lieu sûr et en bon état pendant toute la durée de la location, le protéger des intempéries et du vol, et le restituer dans l'état où il l'a reçu, compte tenu de l'usure normale.

L'usure normale s'entend de la détérioration résultant d'un usage normal et soigneux. Elle ne couvre pas les dommages résultant d'un usage abusif, d'une négligence, d'une surcharge, d'une utilisation dans des conditions auxquelles le bien n'est pas destiné, ou du non-respect des instructions du fabricant.

Le Locataire doit informer le Loueur dès qu'une anomalie survient et cesser d'utiliser le bien dès l'apparition d'un défaut qui le rend dangereux.`
    ),
  },

  lossDamage: {
    heading: t2("Loss, damage and theft", "Perte, dommages et vol"),
    body: t2(
      `The Renter is responsible for the item from the moment it is handed over until the moment the Owner takes it back and checks it in.

If the item is damaged, lost or stolen during that period, the Renter is liable for the cost of repair or, where it cannot economically be repaired, its replacement value, together with the Owner's reasonable loss of rental income while it is out of service.

Theft or malicious damage must be reported to the police and to the Owner as soon as the Renter becomes aware of it, and the Renter must give the Owner the report reference.`,
      `Le Locataire répond du bien depuis sa remise jusqu'à sa reprise et son contrôle effectifs par le Loueur.

Si le bien est endommagé, perdu ou volé pendant cette période, le Locataire est redevable du coût de la réparation ou, lorsque celle-ci n'est pas économiquement justifiée, de la valeur de remplacement du bien, majorée de la perte de revenus locatifs raisonnablement subie par le Loueur pendant son immobilisation.

Tout vol ou acte de vandalisme doit être signalé aux services de police et au Loueur dès que le Locataire en a connaissance, le Locataire devant communiquer au Loueur la référence du procès-verbal.`
    ),
  },

  insurance: {
    heading: t2("Insurance", "Assurance"),
    body: t2(
      `The Renter must be covered by insurance appropriate to the item and its use for the whole rental period, and must produce evidence of that cover on request.

Where the Owner offers cover or a damage waiver, its terms, exclusions and excess are set out separately and form part of this agreement. A damage waiver is not insurance and does not remove the Renter's liability where the waiver's own conditions are broken.

Insurance does not respond where the item was used in breach of this agreement.`,
      `Le Locataire doit être couvert, pendant toute la durée de la location, par une assurance adaptée au bien et à son usage, et en justifier sur demande.

Lorsque le Loueur propose une garantie ou un rachat de franchise, ses conditions, exclusions et franchises sont énoncées séparément et font partie du présent contrat. Un rachat de franchise n'est pas une assurance et ne supprime pas la responsabilité du Locataire en cas de manquement à ses propres conditions.

Aucune assurance ne joue lorsque le bien a été utilisé en violation du présent contrat.`
    ),
  },

  indemnity: {
    heading: t2("Indemnity and limitation of liability", "Garantie et limitation de responsabilité"),
    body: t2(
      `The Renter will indemnify the Owner against all claims, damages, losses, fines and expenses arising out of the Renter's use of the item, except to the extent they are caused by the Owner's own negligence.

The Owner is not liable for indirect or consequential loss, including lost profit, lost time, missed events, or the cost of alternative arrangements, arising from the item being unavailable, delayed or defective.

Nothing in this agreement limits either party's liability for death or personal injury caused by negligence, for fraud, or for anything else that cannot lawfully be limited.`,
      `Le Locataire garantit le Loueur contre toute réclamation, tout dommage, toute perte, toute amende et tous frais résultant de l'usage du bien par le Locataire, sauf dans la mesure où ils sont imputables à la faute du Loueur.

Le Loueur n'est pas responsable des dommages indirects ou consécutifs, notamment la perte de bénéfice, la perte de temps, la perte d'un événement ou le coût d'une solution de remplacement, résultant de l'indisponibilité, du retard ou de la défectuosité du bien.

Aucune stipulation du présent contrat ne limite la responsabilité de l'une ou l'autre partie en cas de décès ou de dommage corporel causé par une faute, en cas de fraude, ni dans les autres cas où la loi interdit une telle limitation.`
    ),
  },

  lateReturn: {
    heading: t2("Late return", "Retard de restitution"),
    body: t2(
      `If the item is not returned by {{ends_at}} and no extension has been agreed in writing, the Renter is charged for each additional period at the applicable rate, and is liable for any loss the Owner suffers because the item was promised to another customer.

An item that is significantly overdue, and whose Renter cannot be reached, may be treated by the Owner as unlawfully retained and reported as such.`,
      `Si le bien n'est pas restitué le {{ends_at}} et qu'aucune prolongation n'a été convenue par écrit, le Locataire est facturé de chaque période supplémentaire au tarif applicable et répond du préjudice subi par le Loueur du fait de la réservation du bien par un autre client.

Un bien restitué avec un retard important, dont le Locataire demeure injoignable, peut être considéré par le Loueur comme retenu sans droit et signalé comme tel.`
    ),
  },

  cancellation: {
    heading: t2("Cancellation", "Annulation"),
    body: t2(
      `Cancellation and refunds are governed by the Owner's published cancellation policy for this booking, which was shown to the Renter at the time of booking and forms part of this agreement.

The Owner may cancel before handover, refunding everything paid, where the item has become unavailable, unsafe or unlawful to rent.`,
      `L'annulation et le remboursement sont régis par la politique d'annulation du Loueur applicable à la présente réservation, portée à la connaissance du Locataire lors de la réservation et faisant partie intégrante du présent contrat.

Le Loueur peut annuler avant la remise, en remboursant l'intégralité des sommes versées, lorsque le bien est devenu indisponible, dangereux ou impossible à louer légalement.`
    ),
  },

  default: {
    heading: t2("Default and recovery", "Défaillance et reprise du bien"),
    body: t2(
      `The Owner may end this agreement immediately and take the item back if the Renter breaks a material term of it, if the item is being used in a way that risks damage or injury, if a payment due under it is not made, or if the Renter gave false information to obtain the rental.

The Renter agrees that in those circumstances the Owner, or someone acting for the Owner, may recover the item, and will give the Owner access for that purpose. The Owner will exercise this right lawfully and will account to the Renter for anything found with the item.`,
      `Le Loueur peut résilier le présent contrat avec effet immédiat et reprendre le bien si le Locataire manque à une obligation essentielle, si le bien est utilisé d'une manière susceptible de causer un dommage ou une blessure, si un paiement exigible n'est pas effectué, ou si le Locataire a obtenu la location au moyen de renseignements inexacts.

Le Locataire accepte que, dans ces circonstances, le Loueur ou son mandataire puisse reprendre possession du bien, et lui en donnera accès à cette fin. Le Loueur exerce ce droit dans le respect de la loi et rend compte au Locataire des effets trouvés avec le bien.`
    ),
  },

  privacy: {
    heading: t2("Personal information", "Renseignements personnels"),
    body: t2(
      `The Owner collects and holds the Renter's personal information, including identification documents, in order to perform this agreement, to meet legal obligations, and to recover amounts owed.

It is kept for as long as the Owner is required to keep it and is not sold. The Renter may ask the Owner what is held, ask for it to be corrected, and ask for it to be deleted where the Owner is no longer required to keep it.`,
      `Le Loueur recueille et conserve les renseignements personnels du Locataire, y compris ses pièces d'identité, aux fins de l'exécution du présent contrat, du respect de ses obligations légales et du recouvrement des sommes dues.

Ces renseignements sont conservés aussi longtemps que la loi l'exige et ne sont pas vendus. Le Locataire peut demander au Loueur quels renseignements sont détenus, en demander la rectification et en demander la suppression lorsque le Loueur n'est plus tenu de les conserver.`
    ),
  },

  governingLaw: {
    heading: t2("Governing law", "Droit applicable"),
    body: t2(
      `This agreement is governed by the law of {{governing_law}}, and the parties submit to the courts of that place.`,
      `Le présent contrat est régi par le droit de {{governing_law}}, et les parties reconnaissent la compétence des tribunaux de ce ressort.`
    ),
  },

  entire: {
    heading: t2("Entire agreement", "Intégralité du contrat"),
    body: t2(
      `This document, together with the booking, the cancellation policy and any condition report, is the whole agreement between the parties about this rental, and replaces anything said or written before it.

If any part of it is found to be unenforceable, the rest continues to apply. A delay by the Owner in enforcing a term is not a waiver of it.`,
      `Le présent document, avec la réservation, la politique d'annulation et tout état des lieux, constitue l'intégralité de l'accord des parties relatif à la présente location et remplace tout échange antérieur, verbal ou écrit.

Si l'une de ses stipulations est jugée non exécutoire, les autres demeurent applicables. La tolérance du Loueur quant à l'exécution d'une obligation ne vaut pas renonciation à celle-ci.`
    ),
  },

  // The clause that makes the electronic signature worth having. ESIGN and UETA
  // ask for consent to transact electronically as something the signer agrees
  // to, distinctly from the signature itself -- which is why the signing page
  // records signer_consented_at in its own column rather than inferring it.
  esign: {
    heading: t2("Signature and electronic records", "Signature et documents électroniques"),
    body: t2(
      `The Renter agrees that this agreement may be signed electronically and that electronic records satisfy any requirement for a document to be in writing.

By signing, the Renter confirms that they have read and understood this agreement, that they intend to be bound by it, and that they consent to receiving this agreement and related notices electronically. A copy is provided to the Renter at the address on file and remains available to them.

The Renter may ask for a paper copy at any time, and may withdraw consent to electronic records for future dealings by telling the Owner.`,
      `Le Locataire accepte que le présent contrat soit signé par voie électronique et que les documents électroniques satisfassent à toute exigence d'écrit.

En signant, le Locataire confirme avoir lu et compris le présent contrat, entendre s'y obliger et consentir à recevoir ce contrat et les avis qui s'y rapportent par voie électronique. Une copie lui est transmise à l'adresse figurant au dossier et lui demeure accessible.

Le Locataire peut demander une copie papier à tout moment et retirer son consentement aux documents électroniques pour l'avenir en en informant le Loueur.`
    ),
  },
};

// ----------------------------------------------------------------------------
// The liability waiver
// ----------------------------------------------------------------------------
// A second, shorter document rather than a clause inside the first. Operators
// in the active trades want it signed on its own, sometimes by a participant
// who is not the person paying -- and a court reads a separately signed
// acknowledgement of risk differently from clause 14 of a long form.
//
// `risks` is the one sentence that has to change per trade: a waiver that does
// not name the actual hazard is the kind a judge discounts.

export const waiverDocument = ({ risks }) => ({
  docKey: "liability_waiver",
  kind: "waiver",
  title: t2("Assumption of Risk and Release of Liability", "Acceptation des risques et décharge de responsabilité"),
  clauses: [
    {
      heading: t2("Who is signing", "Signataire"),
      body: t2(
        `This release is given on {{today}} by {{renter_name}} to {{business_name}} in connection with the rental of {{asset}} under reference {{reservation_number}}.`,
        `La présente décharge est consentie le {{today}} par {{renter_name}} à {{business_name}} dans le cadre de la location de {{asset}}, référence {{reservation_number}}.`
      ),
    },
    {
      heading: t2("The risks I am accepting", "Risques acceptés"),
      body: risks,
    },
    {
      heading: t2("I am fit to do this", "Aptitude"),
      body: t2(
        `I confirm that I am physically and mentally able to use the item safely, that I am not under the influence of alcohol, drugs or any medication that impairs my ability to do so, and that I have told the business about any condition that could affect my safety or anyone else's.

I confirm that I have received instruction on the safe use of the item and have had the chance to ask questions.`,
        `Je confirme être physiquement et mentalement apte à utiliser le bien en toute sécurité, ne pas être sous l'influence de l'alcool, de drogues ou d'un médicament altérant cette aptitude, et avoir informé l'entreprise de toute condition susceptible d'affecter ma sécurité ou celle d'autrui.

Je confirme avoir reçu les consignes d'utilisation en sécurité du bien et avoir eu la possibilité de poser des questions.`
      ),
    },
    {
      heading: t2("Release", "Décharge"),
      body: t2(
        `Knowing these risks, I agree to release {{business_name}}, its owners, staff and agents from claims for injury, loss or damage that I suffer arising out of my use of the item, except where caused by their own negligence or by their failure to meet a duty the law does not allow them to exclude.

I understand that this is a legal document, that I am giving up rights by signing it, and that I am signing it freely.

Nothing in this release limits liability for death or personal injury caused by negligence, or for anything else that cannot lawfully be released.`,
        `Connaissant ces risques, j'accepte de décharger {{business_name}}, ses propriétaires, son personnel et ses mandataires de toute réclamation pour blessure, perte ou dommage que je subirais du fait de mon utilisation du bien, sauf lorsqu'ils résultent de leur propre faute ou du manquement à une obligation que la loi ne permet pas d'exclure.

Je comprends qu'il s'agit d'un document juridique, que je renonce à des droits en le signant, et que je le signe librement.

Aucune stipulation de la présente décharge ne limite la responsabilité en cas de décès ou de dommage corporel causé par une faute, ni dans les autres cas où la loi interdit une telle décharge.`
      ),
    },
    {
      heading: t2("Signature and electronic records", "Signature et documents électroniques"),
      body: t2(
        `I agree that this release may be signed electronically, and I consent to receiving it and related notices electronically. I may ask for a paper copy at any time.`,
        `J'accepte que la présente décharge soit signée par voie électronique et je consens à la recevoir, ainsi que les avis qui s'y rapportent, par voie électronique. Je peux demander une copie papier à tout moment.`
      ),
    },
  ],
});

export default { t2, SPINE, SHARED, VERTICAL_SLOT, waiverDocument };
